import { STAFF_LOCALES, type StaffLocale } from '@rental/domain'
import { STAFF_MESSAGES, type StaffMessages } from '@rental/i18n'
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'rental.staff.locale'

/**
 * The staff app speaks Italian and English (PRD). The choice belongs to this
 * browser: on a shared counter iPad it stays as the last person left it.
 */
function initialLocale(): StaffLocale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored && (STAFF_LOCALES as readonly string[]).includes(stored)) {
      return stored as StaffLocale
    }
  } catch {
    // Storage can be refused (private mode): fall through to the browser's language.
  }
  return window.navigator.language.toLowerCase().startsWith('en') ? 'en' : 'it'
}

interface LocaleContextValue {
  locale: StaffLocale
  messages: StaffMessages
  setLocale: (locale: StaffLocale) => void
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<StaffLocale>(initialLocale)
  const messages = STAFF_MESSAGES[locale]

  useEffect(() => {
    document.documentElement.lang = messages.meta.htmlLang
    document.title = messages.meta.title
  }, [messages])

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      messages,
      setLocale: (next) => {
        setLocaleState(next)
        try {
          window.localStorage.setItem(STORAGE_KEY, next)
        } catch {
          // Not remembered across reloads; the switch still works for now.
        }
      },
    }),
    [locale, messages],
  )
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext)
  if (!value) throw new Error('useLocale outside LocaleProvider')
  return value
}
